/**
 * Minimal zip writer, because a browser refuses to hand over a folder's worth of files: Chrome stops
 * a burst of downloads from one click after about ten, so a 15 tile export silently lost a third of
 * itself. One archive is one download.
 *
 * Entries are stored, not deflated. Everything that goes in here is already gzipped, so compressing
 * it again would cost time and win nothing.
 */
export interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_DIRECTORY = 0x06054b50;
/** Store, and a version that says so. */
const METHOD_STORE = 0;
const VERSION_STORE = 10;
/** Names are written as UTF-8, which the format only allows if this flag says so. */
const FLAG_UTF8 = 0x0800;

let crcTable: Uint32Array | null = null;

function table(): Uint32Array {
  if (crcTable) return crcTable;
  const built = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let value = i;
    for (let bit = 0; bit < 8; bit++) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    built[i] = value >>> 0;
  }
  crcTable = built;
  return built;
}

export function crc32(bytes: Uint8Array): number {
  const lookup = table();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = lookup[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** MS-DOS stamp: two seconds of resolution, and years counted from 1980. */
function dosStamp(when: Date): { time: number; date: number } {
  const year = Math.max(1980, when.getFullYear());
  return {
    time: (when.getHours() << 11) | (when.getMinutes() << 5) | (when.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate(),
  };
}

export function zipStore(entries: ZipEntry[], modified: Date): Blob {
  const encoder = new TextEncoder();
  const stamp = dosStamp(modified);
  const prepared = entries.map((entry) => ({
    name: encoder.encode(entry.name),
    bytes: entry.bytes,
    crc: crc32(entry.bytes),
  }));

  const localSize = prepared.reduce((sum, entry) => sum + 30 + entry.name.length + entry.bytes.length, 0);
  const directorySize = prepared.reduce((sum, entry) => sum + 46 + entry.name.length, 0);
  const buffer = new Uint8Array(localSize + directorySize + 22);
  const view = new DataView(buffer.buffer);
  const offsets: number[] = [];
  let at = 0;

  for (const entry of prepared) {
    offsets.push(at);
    view.setUint32(at, LOCAL_HEADER, true);
    view.setUint16(at + 4, VERSION_STORE, true);
    view.setUint16(at + 6, FLAG_UTF8, true);
    view.setUint16(at + 8, METHOD_STORE, true);
    view.setUint16(at + 10, stamp.time, true);
    view.setUint16(at + 12, stamp.date, true);
    view.setUint32(at + 14, entry.crc, true);
    view.setUint32(at + 18, entry.bytes.length, true);
    view.setUint32(at + 22, entry.bytes.length, true);
    view.setUint16(at + 26, entry.name.length, true);
    view.setUint16(at + 28, 0, true);
    buffer.set(entry.name, at + 30);
    buffer.set(entry.bytes, at + 30 + entry.name.length);
    at += 30 + entry.name.length + entry.bytes.length;
  }

  const directoryAt = at;
  for (const [index, entry] of prepared.entries()) {
    view.setUint32(at, CENTRAL_HEADER, true);
    view.setUint16(at + 4, VERSION_STORE, true);
    view.setUint16(at + 6, VERSION_STORE, true);
    view.setUint16(at + 8, FLAG_UTF8, true);
    view.setUint16(at + 10, METHOD_STORE, true);
    view.setUint16(at + 12, stamp.time, true);
    view.setUint16(at + 14, stamp.date, true);
    view.setUint32(at + 16, entry.crc, true);
    view.setUint32(at + 20, entry.bytes.length, true);
    view.setUint32(at + 24, entry.bytes.length, true);
    view.setUint16(at + 28, entry.name.length, true);
    view.setUint16(at + 30, 0, true);
    view.setUint16(at + 32, 0, true);
    view.setUint16(at + 34, 0, true);
    view.setUint16(at + 36, 0, true);
    view.setUint32(at + 38, 0, true);
    view.setUint32(at + 42, offsets[index], true);
    buffer.set(entry.name, at + 46);
    at += 46 + entry.name.length;
  }

  view.setUint32(at, END_OF_DIRECTORY, true);
  view.setUint16(at + 4, 0, true);
  view.setUint16(at + 6, 0, true);
  view.setUint16(at + 8, prepared.length, true);
  view.setUint16(at + 10, prepared.length, true);
  view.setUint32(at + 12, directorySize, true);
  view.setUint32(at + 16, directoryAt, true);
  view.setUint16(at + 20, 0, true);

  return new Blob([buffer as BlobPart], { type: 'application/zip' });
}
